import axios from "axios";

import { detailMessage } from "@/lib/api/errors";

/** Trigger a browser download of a Blob under the given filename. */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/**
 * Extract the filename a server suggested via `Content-Disposition`.
 *
 * Handles the RFC 6266 forms in the order browsers do: the RFC 5987
 * `filename*=UTF-8''…` parameter wins over the plain `filename="…"` /
 * `filename=…` forms. Returns null when the header is absent (e.g. not
 * exposed cross-origin), not a string, or carries no parsable filename —
 * callers fall back to a generated name.
 */
export const filenameFromContentDisposition = (header: unknown): string | null => {
  if (typeof header !== "string" || header.length === 0) {
    return null;
  }
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/.exec(header);
  if (quoted?.[1]) {
    return quoted[1];
  }
  const bare = /filename\s*=\s*([^;]+)/.exec(header);
  if (bare?.[1]) {
    return bare[1].trim();
  }
  return null;
};

/**
 * The parsed JSON body of a failed blob request, or null.
 *
 * When a request uses `responseType: "blob"`, axios delivers the JSON error
 * body as a Blob too, so `error.response.data` is never directly readable:
 * the Blob has to be read and parsed. Returns null when the error carries no
 * JSON body (network failure, HTML error page, …).
 */
export const readBlobErrorData = async (error: unknown): Promise<unknown> => {
  if (!axios.isAxiosError(error) || !(error.response?.data instanceof Blob)) {
    return null;
  }
  try {
    return JSON.parse(await error.response.data.text()) as unknown;
  } catch {
    return null;
  }
};

/**
 * Extract the backend's `detail` message from a failed blob request: a
 * string `detail`, or the `message` of a structured one. Null when there is
 * no readable message.
 */
export const readBlobErrorDetail = async (error: unknown): Promise<string | null> => {
  const parsed = await readBlobErrorData(error);
  if (parsed && typeof parsed === "object" && "detail" in parsed) {
    return detailMessage((parsed as { detail: unknown }).detail) ?? null;
  }
  return null;
};
